import React, { useState, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { 
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext
} from "@/components/ui/carousel";
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { ExternalLink, Maximize2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import useEmblaCarousel from 'embla-carousel-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
} from "@/components/ui/dialog";

// Interface for a single advertisement
export interface Advertisement {
  id: string;
  title: string;
  description: string;
  imageUrl?: string;
  linkUrl: string;
  backgroundColor?: string;
  textColor?: string;
  gradient?: string;
  company: string;
  is_active?: boolean;
  start_date?: string;
  end_date?: string;
  page_slug?: string;
}

interface AdBannerProps {
  className?: string;
  advertisements?: Advertisement[];
  showFullImages?: boolean;
  pageSlug?: string;
}

const AdBanner: React.FC<AdBannerProps> = ({ 
  className,
  advertisements,
  showFullImages = false,
  pageSlug
}) => {
  const [ads, setAds] = useState<Advertisement[]>(advertisements || []);
  const [currentAdIndex, setCurrentAdIndex] = useState(0);
  const [emblaApi, setEmblaApi] = useState<ReturnType<typeof useEmblaCarousel>[1] | null>(null);
  const [loading, setLoading] = useState(!advertisements);
  const [selectedImage, setSelectedImage] = useState<string | undefined>(undefined);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedAd, setSelectedAd] = useState<Advertisement | null>(null);

  // Configure embla carousel to loop
  const options = { 
    loop: true, 
    align: "start" as const
  };

  // Filter ads based on start and end dates
  const filterActiveAds = (adsList: Advertisement[]) => {
    const today = new Date().toISOString().split('T')[0]; // Get today's date in YYYY-MM-DD format
    
    return adsList.filter(ad => {
      const isActive = ad.is_active === true;
      const isWithinDateRange = (!ad.start_date || ad.start_date <= today) && 
                               (!ad.end_date || ad.end_date >= today);
      
      return isActive && isWithinDateRange;
    });
  };

  // Filter ads by page slug
  const filterAdsByPage = (adsList: Advertisement[]) => {
    if (!pageSlug) return adsList;
    
    return adsList.filter(ad => {
      // Show ads with matching page_slug or ads with no page_slug specified (global ads)
      return !ad.page_slug || ad.page_slug === pageSlug;
    });
  };

  // Fetch ads from Supabase if not provided
  useEffect(() => {
    if (advertisements) {
      let filteredAds = filterActiveAds(advertisements);
      filteredAds = filterAdsByPage(filteredAds);
      setAds(filteredAds);
      return;
    }

    const fetchAds = async () => {
      try {
        let query = supabase
          .from('advertisements')
          .select('*')
          .eq('is_active', true);

        // Add page slug filter if provided
        if (pageSlug) {
          query = query.or(`page_slug.is.null,page_slug.eq.${pageSlug}`);
        }

        const { data, error } = await query.order('created_at', { ascending: false });

        if (error) {
          console.error('Error fetching advertisements:', error);
          setAds([]);
          return;
        }

        if (data && data.length > 0) {
          // Transform database column names to match our component props
          const formattedData = data.map(ad => ({
            id: ad.id,
            title: ad.title,
            description: ad.description,
            company: ad.company,
            imageUrl: ad.image_url,
            linkUrl: ad.link_url,
            backgroundColor: ad.background_color,
            textColor: ad.text_color,
            gradient: ad.gradient,
            start_date: ad.start_date,
            end_date: ad.end_date,
            is_active: ad.is_active,
            page_slug: ad.page_slug
          }));
          
          // Filter ads by date range
          const filteredAds = filterActiveAds(formattedData);
          setAds(filteredAds);
        } else {
          setAds([]);
        }
      } catch (err) {
        console.error('Failed to fetch advertisements:', err);
        setAds([]);
      } finally {
        setLoading(false);
      }
    };

    fetchAds();
  }, [advertisements, pageSlug]);

  // Auto-rotate ads every 5 seconds
  useEffect(() => {
    if (ads.length <= 1) return; // Don't set up rotation if there's only one or no ads
    
    const interval = setInterval(() => {
      if (emblaApi) {
        emblaApi.scrollNext();
      } else {
        setCurrentAdIndex((prevIndex) => 
          prevIndex === ads.length - 1 ? 0 : prevIndex + 1
        );
      }
    }, 5000);
    
    return () => clearInterval(interval);
  }, [emblaApi, ads.length]);

  // Function to track ad clicks
  const handleAdClick = (adId: string, company: string, url: string) => {
    console.log(`Ad clicked: ${adId} from ${company}`);
    // Here you would typically implement analytics tracking
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  // Function to open image in dialog
  const handleImageClick = (e: React.MouseEvent, imageUrl?: string, ad?: Advertisement) => {
    e.stopPropagation(); // Prevent triggering the ad click handler
    if (imageUrl) {
      setSelectedImage(imageUrl);
      setSelectedAd(ad || null);
      setIsDialogOpen(true);
    }
  };

  if (loading) {
    return (
      <div className={cn("mb-6 h-24 animate-pulse bg-gray-200 rounded-md", className)} />
    );
  }

  if (ads.length === 0) {
    return null; // Don't display anything if no ads
  }

  return (
    <div 
      className={cn(
        "mb-6 animate-scale-in",
        className
      )}
    >
      <Carousel 
        className="w-full" 
        opts={options}
        setApi={setEmblaApi}
      >
        <CarouselContent>
          {ads.map((ad) => (
            <CarouselItem key={ad.id}>
              {showFullImages && ad.imageUrl ? (
                // Display image ad as a full image with overlay text
                <Card 
                  className="cursor-pointer overflow-hidden border-0 shadow-lg h-48 relative"
                  onClick={() => handleAdClick(ad.id, ad.company, ad.linkUrl)}
                >
                  <img 
                    src={ad.imageUrl} 
                    alt={ad.title} 
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent flex flex-col justify-end p-4">
                    <h3 className="text-xl font-bold mb-1 text-white">{ad.title}</h3>
                    <p className="mb-2 opacity-90 text-white text-sm">{ad.description}</p>
                    <div className="flex items-center justify-start gap-2">
                      <Button 
                        size="sm" 
                        variant="outline" 
                        className="gap-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-white/40"
                      >
                        Learn More <ExternalLink size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="ml-auto text-white hover:bg-white/20"
                        onClick={(e) => handleImageClick(e, ad.imageUrl, ad)}
                        title="View full image"
                      >
                        <Maximize2 size={16} />
                      </Button>
                    </div>
                  </div>
                </Card>
              ) : (
                // Default ad display with background color/gradient
                <Card 
                  className="cursor-pointer overflow-hidden border-0 shadow-lg h-40"
                  style={{ 
                    background: ad.imageUrl ? `url(${ad.imageUrl}) center/cover no-repeat` : 
                               (ad.gradient || ad.backgroundColor || 'white'),
                    color: ad.textColor || 'inherit'
                  }}
                  onClick={() => handleAdClick(ad.id, ad.company, ad.linkUrl)}
                >
                  <CardContent className="p-4 flex flex-col md:flex-row items-center gap-4 bg-black/30 backdrop-blur-sm h-full">
                    <div className="flex-grow text-center md:text-left">
                      <h3 className="text-xl font-bold mb-1 text-white">{ad.title}</h3>
                      <p className="mb-2 opacity-90 text-white text-sm">{ad.description}</p>
                      <div className="flex items-center justify-center md:justify-start gap-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          className="gap-1 bg-white/20 hover:bg-white/30 backdrop-blur-sm text-white border-white/40"
                        >
                          Learn More <ExternalLink size={14} />
                        </Button>
                        {ad.imageUrl && (
                          <Button
                            size="sm"
                            variant="ghost"
                            className="ml-auto text-white hover:bg-white/20"
                            onClick={(e) => handleImageClick(e, ad.imageUrl, ad)}
                            title="View full image"
                          >
                            <Maximize2 size={16} />
                          </Button>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
        <CarouselPrevious className="left-2 bg-white/70 hover:bg-white" />
        <CarouselNext className="right-2 bg-white/70 hover:bg-white" />
      </Carousel>

      {/* Image Dialog */}
      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-[95vw] max-h-[95vh] w-fit h-fit p-0 overflow-hidden">
          <div className="relative">
            {selectedImage && (
              <img 
                src={selectedImage} 
                alt={selectedAd?.title || "Advertisement"} 
                className="max-h-[85vh] max-w-[90vw] w-auto h-auto object-contain"
              />
            )}
            
            {/* Overlay with ad information */}
            {selectedAd && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/90 via-black/70 to-transparent p-4">
                <div className="text-white">
                  <h3 className="text-lg md:text-xl font-bold mb-1">{selectedAd.title}</h3>
                  <p className="text-sm md:text-base opacity-90 mb-3">{selectedAd.description}</p>
                  <p className="text-xs md:text-sm opacity-75 mb-3">{selectedAd.company}</p>
                  
                  <div className="flex items-center gap-3">
                    <Button
                      onClick={() => {
                        handleAdClick(selectedAd.id, selectedAd.company, selectedAd.linkUrl);
                        setIsDialogOpen(false);
                      }}
                      size="sm"
                      className="bg-primary hover:bg-primary/90 text-primary-foreground"
                    >
                      <ExternalLink className="w-4 h-4 mr-2" />
                      Visiter le site
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdBanner;
